import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, yupResolver } from '@mantine/form';
import dayjs from 'dayjs';

import {
    useGetAccessibleAdvertisingZones,
    useGetAccessibleSurveyStores,
    useGetStoreManagers,
    useGetSurveyProductListsLazy,
    useGetSurveyProductsByImportLogLazy,
    useCreateSurvey,
} from '../../../services/survey';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../../notifications';
import { getErrorMessage } from '../../../pages/price-surveys/helpers';
import {
    getActiveScopeId,
    getProductListPlaceholder,
    getSurveyScopeQueryInput,
    mapManagersToOptions,
    mapProductListsToOptions,
    mapStoresToOptions,
    mapZonesToOptions,
} from './helpers';
import {
    DUE_DATETIME_PAST_MESSAGE,
    getDueDateTime,
    requestSurveyInitialValues,
    requestSurveyValidationSchema,
} from './requestSurveyForm';
import {
    ProductListOption,
    RequestSurveyFormValues,
    ResolvedSurveyProducts,
    SurveyScopeKind,
} from './types';

export function useRequestSurveyModal(opened: boolean, close: () => void) {
    const latestListRequestRef = useRef(0);
    const latestProductsRequestRef = useRef(0);
    const hasAppliedInitialScopeRef = useRef(false);

    const [productLists, setProductLists] = useState<ProductListOption[]>([]);
    const [resolvedProducts, setResolvedProducts] =
        useState<ResolvedSurveyProducts | null>(null);
    const [listsLoading, setListsLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);

    const form = useForm<RequestSurveyFormValues>({
        initialValues: requestSurveyInitialValues,
        validate: yupResolver(requestSurveyValidationSchema),
        validateInputOnBlur: true,
    });

    const isZoneScope = form.values.scopeKind === 'ZONE';
    const activeScopeId = getActiveScopeId(
        form.values.scopeKind,
        form.values.advertisingZoneId,
        form.values.storeId,
    );

    const { data: advertisingZonesData, loading: loadingZones } =
        useGetAccessibleAdvertisingZones(!opened);

    const { data: surveyStoresData, loading: loadingStores } =
        useGetAccessibleSurveyStores(!opened);

    const { data: storeManagersData, loading: loadingManagers } =
        useGetStoreManagers({
            skip: !opened || !activeScopeId,
            advertisingZoneId: isZoneScope
                ? form.values.advertisingZoneId
                : undefined,
            storeId: !isZoneScope ? form.values.storeId : undefined,
        });

    const [fetchProductLists] = useGetSurveyProductListsLazy();
    const [fetchProductsByImportLog] = useGetSurveyProductsByImportLogLazy();
    const [createSurvey, { loading: submitting }] = useCreateSurvey();

    const advertisingZones =
        advertisingZonesData?.getAccessibleAdvertisingZones || [];
    const surveyStores = surveyStoresData?.getAccessibleSurveyStores || [];
    const storeManagers = storeManagersData?.getUsers || [];

    const hasZones = advertisingZones.length > 0;
    const hasStores = surveyStores.length > 0;
    const scopesStillLoading =
        opened && (loadingZones || loadingStores) && !hasZones && !hasStores;

    const resetDependentScopeFields = useCallback(() => {
        form.setFieldValue('productListId', '');
        form.setFieldValue('championUserId', '');
        setProductLists([]);
        setResolvedProducts(null);
    }, [form]);

    useEffect(() => {
        if (!opened) {
            hasAppliedInitialScopeRef.current = false;
            return;
        }

        if (
            loadingZones ||
            loadingStores ||
            hasAppliedInitialScopeRef.current
        ) {
            return;
        }

        hasAppliedInitialScopeRef.current = true;

        if (!hasZones && hasStores) {
            form.setFieldValue('scopeKind', 'STORE');
            form.setFieldValue('advertisingZoneId', '');
            return;
        }

        if (hasZones && !hasStores) {
            form.setFieldValue('scopeKind', 'ZONE');
            form.setFieldValue('storeId', '');
        }
    }, [opened, loadingZones, loadingStores, hasZones, hasStores]);

    useEffect(() => {
        if (!opened || !activeScopeId || !form.values.surveyType) {
            setProductLists([]);
            setResolvedProducts(null);
            setListsLoading(false);
            return;
        }

        latestListRequestRef.current += 1;
        const requestId = latestListRequestRef.current;
        const { surveyType, scopeKind } = form.values;

        setListsLoading(true);
        setProductLists([]);
        setResolvedProducts(null);
        form.setFieldValue('productListId', '');

        fetchProductLists({
            variables: {
                input: {
                    ...getSurveyScopeQueryInput(scopeKind, activeScopeId),
                    surveyType,
                },
            },
        })
            .then(result => {
                if (requestId !== latestListRequestRef.current) {
                    return;
                }
                setListsLoading(false);

                if (result.error) {
                    setProductLists([]);
                    form.setFieldValue('productListId', '');
                    return;
                }

                const lists = result.data?.getSurveyProductLists?.lists ?? [];
                setProductLists(lists);

                if (lists.length === 1) {
                    form.setFieldValue(
                        'productListId',
                        lists[0].manualDataImportLogId,
                    );
                } else {
                    form.setFieldValue('productListId', '');
                }
            })
            .catch(() => {
                if (requestId !== latestListRequestRef.current) {
                    return;
                }
                setListsLoading(false);
                setProductLists([]);
                form.setFieldValue('productListId', '');
            });
    }, [
        opened,
        activeScopeId,
        form.values.scopeKind,
        form.values.surveyType,
        fetchProductLists,
    ]);

    useEffect(() => {
        const { surveyType, productListId, scopeKind } = form.values;

        if (!opened || !activeScopeId || !productListId) {
            setResolvedProducts(null);
            setProductsLoading(false);
            return;
        }

        latestProductsRequestRef.current += 1;
        const requestId = latestProductsRequestRef.current;

        setProductsLoading(true);
        setResolvedProducts(null);

        fetchProductsByImportLog({
            variables: {
                input: {
                    manualDataImportLogId: productListId,
                    ...getSurveyScopeQueryInput(scopeKind, activeScopeId),
                },
            },
        })
            .then(result => {
                if (requestId !== latestProductsRequestRef.current) {
                    return;
                }
                setProductsLoading(false);

                const payload = result.data?.getSurveyProductsByImportLog;
                if (result.error || !payload) {
                    setResolvedProducts(null);
                    return;
                }

                setResolvedProducts({
                    scopeKind,
                    scopeId: activeScopeId,
                    surveyType,
                    manualDataImportLogId: payload.manualDataImportLogId,
                    label: payload.label,
                    products: payload.products.map(product => ({
                        id: product.id,
                        rank: product.rank,
                        sku: product.sku,
                    })),
                });
            })
            .catch(() => {
                if (requestId !== latestProductsRequestRef.current) {
                    return;
                }
                setProductsLoading(false);
                setResolvedProducts(null);
            });
    }, [
        opened,
        activeScopeId,
        form.values.scopeKind,
        form.values.surveyType,
        form.values.productListId,
        fetchProductsByImportLog,
    ]);

    useEffect(() => {
        form.setFieldValue('championUserId', '');
        form.setFieldValue('productListId', '');
    }, [activeScopeId, form.values.surveyType, form.values.scopeKind]);

    const availableZones = useMemo(
        () => mapZonesToOptions(advertisingZones),
        [advertisingZones],
    );
    const availableStores = useMemo(
        () => mapStoresToOptions(surveyStores),
        [surveyStores],
    );
    const availableManagers = useMemo(
        () => mapManagersToOptions(storeManagers),
        [storeManagers],
    );
    const productListOptions = useMemo(
        () => mapProductListsToOptions(productLists),
        [productLists],
    );

    const selectedProductListId = productListOptions.some(
        option => option.value === form.values.productListId,
    )
        ? form.values.productListId
        : '';

    useEffect(() => {
        if (
            form.values.productListId &&
            form.values.productListId !== selectedProductListId
        ) {
            form.setFieldValue('productListId', '');
            setResolvedProducts(null);
        }
    }, [form.values.productListId, selectedProductListId]);

    const handleClose = useCallback(() => {
        form.reset();
        setProductLists([]);
        setResolvedProducts(null);
        close();
    }, [form, close]);

    const handleScopeKindChange = (value: string) => {
        form.setFieldValue('scopeKind', value as SurveyScopeKind);
        form.setFieldValue('advertisingZoneId', '');
        form.setFieldValue('storeId', '');
        resetDependentScopeFields();
    };

    const handleZoneChange = (value: string | null) => {
        form.setFieldValue('advertisingZoneId', value ?? '');
        resetDependentScopeFields();
    };

    const handleStoreChange = (value: string | null) => {
        form.setFieldValue('storeId', value ?? '');
        resetDependentScopeFields();
    };

    const handleSubmit = form.onSubmit(async values => {
        const selectedScopeId =
            values.scopeKind === 'ZONE'
                ? values.advertisingZoneId
                : values.storeId;

        if (
            !resolvedProducts ||
            resolvedProducts.scopeKind !== values.scopeKind ||
            resolvedProducts.scopeId !== selectedScopeId ||
            resolvedProducts.surveyType !== values.surveyType ||
            resolvedProducts.products.length === 0
        ) {
            showErrorNotification(
                'No products found for the selected list. Please ensure a product list upload exists for this scope and survey type.',
            );
            return;
        }

        const dueDateTime = getDueDateTime(values.dueDate, values.dueTime);

        if (!dueDateTime || !dueDateTime.isAfter(dayjs())) {
            showErrorNotification(DUE_DATETIME_PAST_MESSAGE);
            return;
        }

        try {
            const products = resolvedProducts.products.map(product => ({
                rank: product.rank,
                sku: product.sku,
            }));

            const { data } = await createSurvey({
                variables: {
                    input: {
                        ...getSurveyScopeQueryInput(
                            values.scopeKind,
                            selectedScopeId,
                        ),
                        manualDataImportLogId:
                            resolvedProducts.manualDataImportLogId,
                        products,
                        dueBy: dueDateTime.toISOString(),
                        championUserId: values.championUserId,
                    },
                },
            });

            const response = data?.createSurvey;

            if (response?.surveys?.length) {
                showSuccessNotification('Survey created successfully');
                handleClose();
            } else if (response?.errors?.length) {
                showErrorNotification(
                    response.errors[0].message || 'Failed to create survey',
                );
            } else {
                showErrorNotification('Failed to create survey');
            }
        } catch (error) {
            showErrorNotification(getErrorMessage(error));
        }
    });

    return {
        form,
        isZoneScope,
        activeScopeId,
        submitting,
        listsLoading,
        productsLoading,
        productCount: resolvedProducts?.products.length ?? 0,
        selectedProductListId,
        productListOptions,
        productListPlaceholder: getProductListPlaceholder({
            listsLoading,
            activeScopeId,
            hasLists: productListOptions.length > 0,
            isZoneScope,
        }),
        availableManagers,
        loadingManagers,
        handleClose,
        handleSubmit,
        handleScopeKindChange,
        handleZoneChange,
        handleStoreChange,
        scopesStillLoading,
        hasZones,
        hasStores,
        availableZones,
        availableStores,
        loadingZones,
        loadingStores,
    };
}
